import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useEffect } from 'react'
import { AxiosError } from 'axios'

import { dismissAllNotifications } from '@/api/actions/notifications/notificationActions'
import { UI_PAGE_SIZE } from '@/api/actions/notifications/notificationConstants'
import { Mutations } from '@/api/mutations.enum'
import { Queries } from '@/api/queries.enum'
import type {
    DismissAllNotificationsResponse,
    NotificationListResponse,
} from '@/api/actions/notifications/notificationActions.types'

interface UseDismissAllNotificationsOptions {
    onSuccess?: (data: DismissAllNotificationsResponse, totalDismissed: number) => void
    onError?: (error: AxiosError) => void
}

export const useDismissAllNotifications = (options?: UseDismissAllNotificationsOptions) => {
    const queryClient = useQueryClient()
    const abortControllerRef = useRef<AbortController | null>(null)

    const mutation = useMutation<
        DismissAllNotificationsResponse,
        AxiosError,
        void,
        { previousNotifications?: NotificationListResponse; previousCount?: number }
    >({
        mutationKey: [Mutations.dismissAllNotifications],
        mutationFn: async () => {
            // Create new AbortController for this operation
            abortControllerRef.current = new AbortController()
            const signal = abortControllerRef.current.signal

            // Using bulk dismiss endpoint
            return await dismissAllNotifications(signal)
        },
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: [Queries.getNotifications] })
            await queryClient.cancelQueries({ queryKey: [Queries.getNotificationCount] })

            const previousNotifications = queryClient.getQueryData([Queries.getNotifications, 1, UI_PAGE_SIZE]) as
                | NotificationListResponse
                | undefined
            const previousCount = queryClient.getQueryData([Queries.getNotificationCount]) as number | undefined

            // Clear ALL notification cache entries to prevent flash of stale data
            queryClient.removeQueries({ queryKey: [Queries.getNotifications] })

            // Set the main notification list to empty
            queryClient.setQueryData([Queries.getNotifications, 1, UI_PAGE_SIZE], {
                results: [],
                count: 0,
                next: null,
                previous: null,
            })

            queryClient.setQueryData([Queries.getNotificationCount], 0)
            return { previousNotifications, previousCount }
        },
        onSuccess: (data) => {
            const totalDismissed = data.dismissed_count

            // No toast here - will be handled by asyncToast in the component
            // No query invalidation needed - optimistic updates handle the UI
            options?.onSuccess?.(data, totalDismissed)
        },
        onError: (error, _, context) => {
            // Don't show error or rollback for aborted operations
            if (error.name === 'AbortError' || error.name === 'CanceledError') {
                return
            }

            if (context?.previousNotifications) {
                queryClient.setQueryData([Queries.getNotifications, 1, UI_PAGE_SIZE], context.previousNotifications)
            }
            if (context?.previousCount !== undefined) {
                queryClient.setQueryData([Queries.getNotificationCount], context.previousCount)
            }

            // No toast here - will be handled by asyncToast in the component
            options?.onError?.(error)
        },
    })

    // Cleanup AbortController on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    return {
        ...mutation,
        abort: () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        },
    }
}
